import { useNavigate } from "react-router-dom";
import {
  AlertOctagon,
  BarChart3,
  BookOpen,
  CheckCircle2,
  CheckSquare,
  Circle,
  Clock,
  FileOutput,
  FileText,
  Info,
  ListChecks,
  Play,
  Target,
  Wrench,
  XCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import {
  useGuideProgress,
  type GuideStep,
} from "@/hooks/api/query/useGuideProgress";
import type {
  GuideStepStatus,
  DocumentChecklistItem,
} from "@/types/guide-step";
import { getIdentificareStatus } from "@/utils/guideProgressCalculator";
import { useIsMobile } from "../hooks/use-mobile";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";

type RoadmapStepConfig = {
  id: number;
  title: string;
  termen: string;
  descriere: string;
  output: string;
  outputType: string;
  icon: React.ElementType;
  action: string;
};

const roadmapStepsConfig: RoadmapStepConfig[] = [
  {
    id: 1,
    title: "Notificarea și Identificarea",
    termen: "30-90 zile",
    descriere:
      "Prima etapă obligatorie. Entitatea se auto-identifică folosind datele financiare și codurile CAEN.",
    output: "Formular de Notificare PDF (semnat și trimis la DNSC)",
    outputType: "PDF pentru DNSC",
    icon: FileText,
    action: "/identificare-raportare",
  },
  {
    id: 2,
    title: "Analiza Efectului Perturbator (Art. 9)",
    termen: "Condiționat",
    descriere:
      "Se activează pentru firmele mici/micro care prestează servicii critice (ex: cloud, MSSP).",
    output: "Raport de Analiză privind Efectul Perturbator PDF",
    outputType: "PDF pentru DNSC",
    icon: AlertOctagon,
    action: "/analiza-efect-perturbator",
  },
  {
    id: 3,
    title: "Analiza de Risc (ENIRE@RO)",
    termen: "60 zile post-decizie",
    descriere:
      "Evaluarea amenințărilor specifice sectorului după primirea deciziei de înregistrare.",
    output:
      "Raport Analiză de Risc PDF (stabilește nivelul de complexitate al măsurilor)",
    outputType: "PDF pentru DNSC",
    icon: Target,
    action: "/analiza-risc",
  },
  {
    id: 4,
    title: "Evaluarea Maturității (CyFun® 2025)",
    termen: "90 zile post-decizie",
    descriere:
      "Autoevaluarea celor 6 funcții de securitate: Guvernare, Identificare, Protecție, Detectare, Răspuns, Recuperare.",
    output: "Fișier Excel format DNSC (EVAL_MMS)",
    outputType: "Excel EVAL_MMS",
    icon: BarChart3,
    action: "/evaluare-maturitate",
  },
  {
    id: 5,
    title: "Planul de Remediere",
    termen: "30 zile post-evaluare",
    descriere:
      "Obligatoriu dacă scorul total este < 3.5 (pentru esențiale) sau dacă măsurile-cheie nu sunt îndeplinite.",
    output: "Plan de măsuri asumat de management",
    outputType: "PDF pentru DNSC",
    icon: Wrench,
    action: "/plan-remediere",
  },
];

function getStatusStyles(status: GuideStepStatus) {
  switch (status) {
    case "completed":
      return {
        iconBg: "bg-blue-600",
        cardRing: "",
        buttonStyle: "bg-blue-100 text-blue-700 hover:bg-blue-200",
        buttonText: "Revizuiește",
        showButton: true,
      };
    case "in_progress":
      return {
        iconBg: "bg-blue-500",
        cardRing: "ring-2 ring-blue-400 ring-offset-2",
        buttonStyle: "bg-blue-600 text-white hover:bg-blue-700",
        buttonText: "Continuă Modulul",
        showButton: true,
      };
    case "not_started":
      return {
        iconBg: "bg-slate-400",
        cardRing: "",
        buttonStyle: "bg-slate-100 text-slate-600 hover:bg-slate-200",
        buttonText: "Începe Modulul",
        showButton: true,
      };
    case "not_applicable":
    default:
      return {
        iconBg: "bg-slate-400",
        cardRing: "",
        buttonStyle: "bg-slate-100 text-slate-400 cursor-not-allowed",
        buttonText: "Nu se aplică",
        showButton: true,
      };
  }
}

function getDocumentIcon(item: DocumentChecklistItem) {
  if (item.status === "provided") {
    return <CheckCircle2 size={18} className="text-blue-500" />;
  }
  if (item.status === "optional") {
    return <CheckSquare size={18} className="text-slate-400" />;
  }
  return <XCircle size={18} className="text-amber-500" />;
}

function RoadmapStepCard({
  config,
  stepData,
  onNavigate,
  entityData,
  lockedMessage,
}: {
  config: RoadmapStepConfig;
  stepData: GuideStep;
  onNavigate: (path: string) => void;
  entityData: boolean;
  lockedMessage?: string;
}) {
  const isMobile = useIsMobile();
  const StepIcon = config.icon;
  const styles = getStatusStyles(stepData.status);
  const isCompleted = stepData.status === "completed";
  const isInProgress = stepData.status === "in_progress";
  const isNotApplicable = stepData.status === "not_applicable";
  const isLocked = !entityData && config.id >= 2;

  return (
    <div className="relative flex md:gap-6 gap-2">
      <div
        className={`relative z-10 md:w-16 w-10 md:h-16 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${isLocked ? "bg-slate-400" : styles.iconBg}`}
      >
        <StepIcon size={isMobile ? 16 : 24} className="text-white" />
        {isCompleted && (
          <div className="absolute -top-1 -right-1 md:w-6 w-4 md:h-6 h-4 bg-blue-700 rounded-full flex items-center justify-center border-2 border-white">
            <CheckCircle2 size={isMobile ? 10 : 14} className="text-white" />
          </div>
        )}
        {isInProgress && (
          <div className="absolute -top-1 -right-1 md:w-6 w-4 md:h-6 h-4 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white animate-pulse">
            <Circle
              size={isMobile ? 8 : 10}
              className="text-white fill-white"
            />
          </div>
        )}
      </div>

      <div
        className={`flex-1 bg-white rounded-2xl md:border-2 border-blue-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${isLocked ? "" : styles.cardRing}`}
      >
        <div className="md:p-4 p-3 bg-blue-50 border-b border-blue-200">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div>
              <span className="text-xs font-bold text-blue-600">
                ETAPA {config.id}
              </span>
              <h3 className="font-heading md:text-lg text-base font-bold text-slate-800">
                {config.title}
              </h3>
            </div>
            <div className="text-right">
              <TouchTooltip content={<p>Termen legal conform OUG 155/2024</p>}>
                <span className="text-xs font-medium text-blue-700 bg-white px-3 py-1 rounded-full flex items-center gap-1 cursor-help">
                  <Clock size={12} />
                  {config.termen}
                </span>
              </TouchTooltip>
            </div>
          </div>
        </div>
        <div className="p-5">
          <p className="md:text-sm text-xs text-slate-600 mb-4">
            {config.descriere}
          </p>

          <div className="flex flex-wrap gap-2 items-center justify-between md:p-3 p-2 bg-slate-50 rounded-xl mb-4">
            <FileOutput size={20} className="text-slate-500" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase font-medium">
                Output
              </p>
              <p className="md:text-sm text-xs font-medium text-slate-700">
                {config.output}
              </p>
            </div>
            <span className="w-full md:w-auto text-center md:text-left text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-700">
              {config.outputType}
            </span>
          </div>

          {styles.showButton && (
            <button
              onClick={() =>
                !isLocked && !isNotApplicable && onNavigate(config.action)
              }
              disabled={isLocked || isNotApplicable}
              className={`w-full md:py-3 py-2 rounded-xl text-sm md:text-base font-medium flex items-center justify-center gap-2 transition-all ${isLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed" : styles.buttonStyle}`}
            >
              <Play size={isMobile ? 16 : 18} />
              {isLocked
                ? (lockedMessage ??
                  "Parcurge pasul 1 de notificare si identificare")
                : styles.buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Skeleton className="w-12 h-12 rounded-xl" />
            <div>
              <Skeleton className="h-7 w-48 mb-2" />
              <Skeleton className="h-5 w-64" />
            </div>
          </div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-6">
              <Skeleton className="w-16 h-16 rounded-full flex-shrink-0" />
              <Skeleton className="flex-1 h-48 rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NoSessionSelected({
  onNavigate,
}: {
  onNavigate: (path: string) => void;
}) {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Info size={24} />
              Selectați o companie
            </CardTitle>
            <CardDescription className="text-amber-700">
              Pentru a vedea progresul conformității NIS2, trebuie să selectați
              sau să creați o companie.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => onNavigate("/identificare-raportare")}
              className="bg-amber-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-amber-700 transition-colors"
            >
              Începe Identificarea
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const GhidNis2 = () => {
  const navigate = useNavigate();
  const { showOnboardingModal } = useOnboardingModal();

  const isMobile = useIsMobile();
  const { currentSessionId, isLoadingSessions } = useSessionSwitcher();
  const { steps, documentChecklist, isLoading, hasSession, entityData } =
    useGuideProgress(currentSessionId);

  const handleNavigate = (action: string) => {
    if (action === "/analiza-efect-perturbator" && !entityData) {
      return;
    }

    if (action === "/analiza-risc") {
      if (
        !entityData ||
        !currentSessionId ||
        getIdentificareStatus(entityData) !== "completed"
      ) {
        return;
      }
      navigate(`/analiza-risc/${currentSessionId}`);
      return;
    }

    if (
      action === "/identificare-raportare" &&
      steps.step1.status === "not_started"
    ) {
      showOnboardingModal();
      return;
    }

    navigate(action);
  };

  if (isLoading || isLoadingSessions) {
    return <LoadingSkeleton />;
  }

  if (!hasSession) {
    return <NoSessionSelected onNavigate={navigate} />;
  }

  const stepDataMap: Record<number, GuideStep> = {
    1: steps.step1,
    2: steps.step2,
    3: steps.step3,
    4: steps.step4,
    5: steps.step5,
  };

  return (
    <div className="container mx-auto px-4 md:py-8 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="md:w-12 w-10 md:h-12 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <BookOpen size={isMobile ? 16 : 24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="font-heading md:text-2xl text-xl font-bold text-slate-800">
                Ghid General NIS2
              </h1>
              <p className="md:text-sm text-xs text-slate-600">
                Roadmap-ul Legal pentru Conformitate
              </p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-2xl border border-blue-200 md:p-5 p-3">
            <div className="flex items-start md:gap-3 gap-2">
              <Info
                className="md:text-2xl text-xl text-blue-600 flex-shrink-0 md:mt-0.5 mt-1"
                size={isMobile ? 16 : 24}
              />
              <div>
                <h3 className="font-heading md:text-lg text-base font-semibold text-blue-800">
                  Despre acest ghid
                </h3>
                <p className="md:text-sm text-xs text-blue-700 mt-1">
                  Acest ghid oferă o viziune de ansamblu asupra întregului
                  parcurs de conformitate NIS2. Fiecare etapă trebuie parcursă
                  în ordine, cu respectarea termenelor legale stabilite de OUG
                  155/2024.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute md:left-8 left-[18px] top-0 bottom-0 w-1 bg-blue-200 rounded-full" />

          <div className="space-y-6">
            {roadmapStepsConfig.map((config) => (
              <RoadmapStepCard
                key={config.id}
                config={config}
                stepData={stepDataMap[config.id]}
                onNavigate={handleNavigate}
                entityData={
                  config.id === 3
                    ? !!entityData &&
                      getIdentificareStatus(entityData) === "completed"
                    : !!entityData
                }
                lockedMessage={
                  config.id === 3
                    ? "Finalizați formularul de notificare pentru a accesa acest modul"
                    : undefined
                }
              />
            ))}
          </div>
        </div>

        <Card className="mt-8">
          <CardHeader className="bg-blue-50 border-b border-blue-100">
            <CardTitle className="flex items-center gap-2 md:text-lg text-base">
              <ListChecks size={isMobile ? 16 : 20} className="text-blue-600" />
              Checklist Documente Necesare
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Documentele necesare pentru parcursul de conformitate
            </CardDescription>
          </CardHeader>
          <CardContent className="md:pt-4 pt-3">
            <div className="grid md:grid-cols-2 gap-3">
              {documentChecklist.map((doc, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg ${
                    doc.status === "provided"
                      ? "bg-blue-50"
                      : doc.status === "optional"
                        ? "bg-slate-50"
                        : "bg-amber-50"
                  }`}
                >
                  {getDocumentIcon(doc)}
                  <span className="md:text-sm text-xs text-slate-700">
                    {doc.text}
                  </span>
                  {doc.required && doc.status === "missing" && (
                    <span className="md:text-xs text-xs text-amber-600 font-medium ml-auto">
                      *Lipsă
                    </span>
                  )}
                  {doc.required && doc.status === "provided" && (
                    <span className="md:text-xs text-xs text-blue-600 font-medium ml-auto">
                      ✓ Furnizat
                    </span>
                  )}
                  {!doc.required && (
                    <span className="md:text-xs text-xs text-slate-500 font-medium ml-auto">
                      Opțional
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GhidNis2;
